import { useTranslation } from 'react-i18next'

import { Badge } from '@/design-system/primitives'
import { ApiError } from '@/lib/apiClient'
import { cn } from '@/lib/cn'

import { useSetSinglePreference, useSinglePreference, type SingleChoice } from './api'

const CHOICES: SingleChoice[] = ['yes', 'no', 'prefer_not_to_say']

/**
 * Le bracelet célibataire, sur la carte du participant.
 *
 * RULE 7 : ce bloc n'est visible que par la personne qui détient le lien de sa
 * commande. Il dit explicitement que la réponse reste privée — sans cette
 * phrase, la question donne l'impression d'être publiée quelque part.
 *
 * Le bracelet est un signal social **volontaire** porté dans la salle. Il n'y a
 * ni liste de célibataires, ni mise en relation, ni messagerie : les gens qui
 * le portent se reconnaissent et se parlent. C'est tout le dispositif.
 */
export function SingleBraceletCard({
  token,
  attendeeId,
}: {
  token: string
  attendeeId: string
}) {
  const { t } = useTranslation()
  const preference = useSinglePreference(token, attendeeId)
  const update = useSetSinglePreference(token, attendeeId)

  const data = preference.data
  if (!data || !data.enabled) return null

  return (
    <div className="mt-5 w-full border-t border-line-subtle pt-5 text-left">
      <p className="overline text-content-muted">{t('single.title')}</p>
      <p className="mt-2 text-sm text-content-secondary">{t('single.question')}</p>

      {data.locked ? (
        <p className="mt-3 text-caption text-content-muted">{t('single.locked')}</p>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          {CHOICES.map((choice) => (
            <button
              key={choice}
              type="button"
              disabled={update.isPending}
              onClick={() => update.mutate(choice)}
              className={cn(
                'rounded-full border px-3 py-1.5 text-caption transition',
                data.choice === choice
                  ? 'border-champagne-400 text-champagne-300'
                  : 'border-line text-content-secondary hover:border-line-strong',
              )}
            >
              {t(`single.choice.${choice}`)}
            </button>
          ))}
        </div>
      )}

      {data.bracelet_status === 'requested' ? (
        <p className="mt-3 text-caption text-content-muted">{t('single.requested')}</p>
      ) : data.bracelet_status === 'distributed' ? (
        <Badge tone="champagne">{t('single.distributed')}</Badge>
      ) : null}

      {/* La phrase la plus importante du bloc. */}
      <p className="mt-3 text-caption text-content-muted">{t('single.privacy')}</p>

      {update.isError ? (
        <p className="mt-2 text-caption text-state-danger">
          {update.error instanceof ApiError ? update.error.message : t('state.errorTitle')}
        </p>
      ) : null}
    </div>
  )
}
